import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from 'axios';
import { API_URLS, BASE_URL, getTeamImageUrl } from "../constants/config";
import { getTokensFromStore, checkIfTokenExpired } from "../utilities/tokens";
import { refreshTokens } from "../actions/usersActions";
import store from "../utilities/store";
import defaultTeamLogo from "../images/defaultTeamLogo.png";
import '../styles/onlineMatch.css';
import { useI18n } from "../utilities/i18n";
import {
  elapsedSecondsSince,
  MATCH_EVENT_TYPES,
  eventLabel,
  eventTypeById,
  isOnFlag,
  resolveYellowCardEventId,
  SECOND_YELLOW_CARD_EVENT_ID,
  absoluteEventMinute,
  formatEventMinuteLabel,
  matchExtraMarkers,
  matchPlayPhase,
  toEventTime,
} from "../utilities/matchesApi";
import {
  adoptServerClock,
  clockEventHint,
  clockMatchesServer,
  hintedTimeNumber,
  loadOnlineMatch,
  makeClock,
  onlineStatusLabelKey,
  pauseClock,
  resumeClock,
  saveOnlineMatch,
  setClockSeconds,
  timeNumberForStatus,
  toggleClock,
  useClockHotkey,
  useOnlineClock,
} from "../utilities/onlineMatchStore";
import MatchLineupSetup from "../components/MatchLineupSetup";
import {
  FORMATIONS_BY_FORMAT,
  MATCH_FORMATS,
  POSITION_LABELS,
  fieldDensityClass,
  generateFieldPositions,
  isLineupSetupComplete,
  lineupToAssignments,
  setupFromServer,
} from "../utilities/lineupSetup";

// ═══════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════

const SCORE_OWN_EVENTS = new Set([1, 6, 16, 18]);
const SCORE_OG_EVENTS = new Set([8]);
const EVENT_STAT = {
  1: 'goals',
  6: 'goals',
  8: 'autogoals',
  3: 'yellow_cards',
  13: 'yellow_cards',
  4: 'red_cards',
  16: 'goals',
  18: 'goals',
};
const LIVE_PHASES = ['half1', 'paused1', 'half2', 'paused2', 'extra1', 'paused_extra1', 'extra2', 'paused_extra2'];
const SYSTEM_EVENT_IDS = [9, 10, 11, 14];

function calcScore(events, homeTeamId) {
  let home = 0, guest = 0;
  events.forEach(e => {
    const type = Number(e.event_type);
    if (SCORE_OWN_EVENTS.has(type)) { e.team_id === homeTeamId ? home++ : guest++; }
    else if (SCORE_OG_EVENTS.has(type)) { e.team_id === homeTeamId ? guest++ : home++; }
  });
  return { home, guest };
}

function collectPlayerStats(events) {
  const playerStats = {};
  const bump = (playerId, field) => {
    if (!playerId) return;
    if (!playerStats[playerId]) playerStats[playerId] = { goals: 0, assists: 0, autogoals: 0, yellow_cards: 0, red_cards: 0 };
    playerStats[playerId][field] += 1;
  };
  events.forEach((e) => {
    if (e.isSystem) return;
    const stat = EVENT_STAT[e.event_type];
    if (stat) bump(e.player_id, stat);
    if (e.assist_player_id) bump(e.assist_player_id, 'assists');
  });
  return playerStats;
}

// ═══════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════

export default function OnlineMatch({ visible, closeModal, matchData, matchApplication }) {
  const { t } = useI18n();

  const getPositionShort = (player, slotIndex) => {
    if (slotIndex === 0) return t('posGK');
    if (player?.position_id) return t(POSITION_LABELS[player.position_id] || '') || '';
    if (player?.match_position) return t(POSITION_LABELS[player.match_position] || '') || '';
    return '';
  };

  // ─── Phase ───
  const [phase, setPhase] = useState('roster');

  // ─── Roster ───
  const [selectedPlayers, setSelectedPlayers] = useState({});

  // ─── Lineup ───
  const [matchFormat, setMatchFormat] = useState('');
  const [homeFormation, setHomeFormation] = useState('');
  const [guestFormation, setGuestFormation] = useState('');
  // Starting lineup: { [slotIndex]: playerId }
  const [homeLineup, setHomeLineup] = useState({});
  const [guestLineup, setGuestLineup] = useState({});
  // ─── Timer ───
  // idle | half1 | paused1 | halftime | half2 | paused2 | extra_* | ended
  // Таймер хранится вне окна (onlineMatchStore) и идёт, даже когда окно закрыто.
  const { clock, seconds: displaySeconds, setClock } = useOnlineClock(matchData?.id, { tick: Boolean(visible) });
  const matchStatus = clock?.status || 'idle';
  const [editingTimer, setEditingTimer] = useState(false);
  const [editMin, setEditMin] = useState('0');
  const [editSec, setEditSec] = useState('0');

  // ─── Events ───
  const [matchEvents, setMatchEvents] = useState([]);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventModalTeamSide, setEventModalTeamSide] = useState('home');
  const [eventType, setEventType] = useState(1);
  const [eventPlayer, setEventPlayer] = useState('');
  const [eventAssist, setEventAssist] = useState('');
  const [eventMinute, setEventMinute] = useState('');
  const [eventSecond, setEventSecond] = useState('');
  const [eventComment, setEventComment] = useState('');
  // { minute, second, time_number } — время с таймера при открытии формы
  const [eventTimeHint, setEventTimeHint] = useState(null);
  const [editingEventId, setEditingEventId] = useState(null);
  // substitution player for event type 15
  const [eventSubPlayer, setEventSubPlayer] = useState('');
  // dragging player id (for live field drag-move/substitution)
  const [draggingPlayer, setDraggingPlayer] = useState(null);
  // ─── Jersey colors ───
  const [homeColor, setHomeColor] = useState('#3498db');
  const [guestColor, setGuestColor] = useState('#e74c3c');

  // ─── Notification ───
  const [notification, setNotification] = useState('');

  // ─── Loading state for server restore ───
  const [isRestoring, setIsRestoring] = useState(false);
  const setupHydratedRef = useRef(false);

  // ═══ DERIVED ═══
  const halfTime = matchData ? parseInt(matchData.half_time) || 30 : 30;
  const homeTeamId = matchData?.home_team_id;
  const guestTeamId = matchData?.guest_team_id;
  const extraAvailable = isOnFlag(matchData?.settings?.extra_time_available ?? matchData?.extra_time_available);
  const extraHalfMin = Number(matchData?.settings?.extra_half_time || matchData?.extra_half_time || 15);
  const extraHalfSec = extraHalfMin * 60;
  const currentMinute = Math.floor(displaySeconds / 60);
  const currentSecond = displaySeconds % 60;
  const score = calcScore(matchEvents, homeTeamId);
  const extraTied = extraAvailable && score.home === score.guest;

  // ═══ SESSION STORAGE (persist setup for one match, clears on page refresh) ═══
  const SESSION_KEY = 'olm_match_setup';

  const saveToSession = (data) => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch (e) { /* ignore */ }
  };

  const loadFromSession = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  };

  const clearSession = () => {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
  };

  // ═══ RESET ON OPEN ═══
  useEffect(() => {
    if (visible && matchApplication && matchData) {
      // Пока состояние не восстановлено, автосохранение не должно перезаписывать сессию.
      setupHydratedRef.current = false;
      // First check if match is already in progress on the server
      const tryRestore = async () => {
        const restored = await restoreMatchFromServer();
        if (restored) { setupHydratedRef.current = true; return; } // Restored from server — done

        // Not in progress on server — check local state (survives closing the window)
        const stored = loadOnlineMatch(matchData.id);
        const saved = stored?.setup
          ? { ...stored.setup, matchId: matchData.id, phase: stored.phase }
          : loadFromSession();
        const sameMatch = saved && Number(saved.matchId) === Number(matchData?.id);
        // Расстановка, собранная в этом окне, главнее серверной; иначе схема с сервера главнее сессии.
        const localFirst = Boolean(stored?.setup) && isLineupSetupComplete(stored.setup);
        const fromServer = setupFromServer(matchData.lineups, matchData.home_team_id, matchData.guest_team_id);
        if (sameMatch || fromServer) {
          const setup = localFirst
            ? { ...(fromServer || {}), ...saved }
            : { ...(sameMatch ? saved : {}), ...(fromServer || {}) };
          const selected = {};
          matchApplication.forEach((p) => { selected[p.id] = !!p.active; });
          setSelectedPlayers(sameMatch && (localFirst || !fromServer) ? (saved.selectedPlayers || selected) : selected);
          // Расстановка уже собрана (например, в блоке составов) — шаги пропускаем.
          setPhase(isLineupSetupComplete(setup) ? 'live' : (sameMatch ? (saved.phase || 'roster') : 'roster'));
          setMatchFormat(setup.matchFormat || '');
          setHomeFormation(setup.homeFormation || '');
          setGuestFormation(setup.guestFormation || '');
          setHomeLineup(setup.homeLineup || {});
          setGuestLineup(setup.guestLineup || {});
          setHomeColor(setup.homeColor || '#3498db');
          setGuestColor(setup.guestColor || '#e74c3c');
        } else {
          clearSession();
          const sel = {};
          matchApplication.forEach(p => { sel[p.id] = !!p.active; });
          setSelectedPlayers(sel);
          setPhase('roster');
          setMatchFormat('');
          setHomeFormation('');
          setGuestFormation('');
          setHomeLineup({});
          setGuestLineup({});
          setHomeColor('#3498db');
          setGuestColor('#e74c3c');
        }
        // Reset transient state for non-restored case
        setClock(makeClock('idle', 0), { sync: false });
        setMatchEvents([]);
        setNotification('');
        setEventModalOpen(false);
        setEditingTimer(false);
        setEditingEventId(null);
        setEventSubPlayer('');
        setDraggingPlayer(null);
        setupHydratedRef.current = true;
      };
      tryRestore();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ AUTO-SAVE setup on change (все фазы, включая live: замены и перестановки тоже) ═══
  useEffect(() => {
    if (!visible || !matchData || !setupHydratedRef.current) return;
    const setup = {
      selectedPlayers,
      matchFormat,
      homeFormation,
      guestFormation,
      homeLineup,
      guestLineup,
      homeColor,
      guestColor,
    };
    saveOnlineMatch(matchData.id, { phase, setup });
    saveToSession({ matchId: matchData.id, phase, ...setup });
  }, [visible, matchData, phase, selectedPlayers, matchFormat, homeFormation, guestFormation, homeLineup, guestLineup, homeColor, guestColor]);

  // ═══ HELPERS ═══
  const getTeamPlayers = useCallback((teamId) => {
    if (!matchApplication) return [];
    return matchApplication.filter(p => p.team_id === teamId || p.isDouble === teamId);
  }, [matchApplication]);

  const getSelectedTeamPlayers = useCallback((teamId) => getTeamPlayers(teamId).filter(p => selectedPlayers[p.id]), [getTeamPlayers, selectedPlayers]);
  const findPlayerById = (id) => matchApplication ? matchApplication.find(p => p.id === id) : null;
  const isMatchActive = () => LIVE_PHASES.includes(matchStatus);

  // Check if player has red card (direct or 2nd yellow) -> banned
  const isPlayerBanned = useCallback((playerId) => {
    const pEvents = matchEvents.filter(e => e.player_id === playerId && !e.isSystem);
    const hasRed = pEvents.some(e => e.event_type === 4);
    const has2ndYellow = pEvents.some(e => e.event_type === 13);
    return hasRed || has2ndYellow;
  }, [matchEvents]);

  // Check if player is currently substituted out (supports reverse subs)
  const isPlayerSubbedOut = useCallback((playerId) => {
    const subsOut = matchEvents.filter(e => e.event_type === 15 && e.player_id === playerId && !e.isSystem).length;
    const subsIn  = matchEvents.filter(e => e.event_type === 15 && e.additional_player_id === playerId && !e.isSystem).length;
    return subsOut > subsIn;
  }, [matchEvents]);

  // Check if player can receive events
  const isPlayerAvailable = useCallback((playerId) => {
    return !isPlayerBanned(playerId) && !isPlayerSubbedOut(playerId);
  }, [isPlayerBanned, isPlayerSubbedOut]);

  // Get bench players (selected but not on field) for live phase
  const getLiveBenchPlayers = useCallback((side) => {
    const teamId = side === 'home' ? homeTeamId : guestTeamId;
    const lineup = side === 'home' ? homeLineup : guestLineup;
    const onFieldIds = new Set(Object.values(lineup));
    return getSelectedTeamPlayers(teamId).filter(p => !onFieldIds.has(p.id) && !isPlayerBanned(p.id));
  }, [homeTeamId, guestTeamId, homeLineup, guestLineup, getSelectedTeamPlayers, isPlayerBanned]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const teamLogo = (img) => getTeamImageUrl(img) || defaultTeamLogo;

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const statusLabel = () => {
    const key = onlineStatusLabelKey(matchStatus);
    return key ? t(key) : '';
  };

  // ═══ SETUP (roster + lineup steps) ═══
  const setupValue = {
    selectedPlayers, matchFormat, homeFormation, guestFormation,
    homeLineup, guestLineup, homeColor, guestColor,
  };

  const applySetupPatch = (patch) => {
    if ('selectedPlayers' in patch) setSelectedPlayers(patch.selectedPlayers);
    if ('matchFormat' in patch) setMatchFormat(patch.matchFormat);
    if ('homeFormation' in patch) setHomeFormation(patch.homeFormation);
    if ('guestFormation' in patch) setGuestFormation(patch.guestFormation);
    if ('homeLineup' in patch) setHomeLineup(patch.homeLineup);
    if ('guestLineup' in patch) setGuestLineup(patch.guestLineup);
    if ('homeColor' in patch) setHomeColor(patch.homeColor);
    if ('guestColor' in patch) setGuestColor(patch.guestColor);
  };

  // ═══ TIMER ACTIONS ═══
  const addSystemEvent = (eventId, seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    setMatchEvents(prev => [...prev, {
      id: Date.now() + Math.random(),
      event_type: eventId,
      player_id: null, player_name: null, player_number: null,
      team_id: null, team_name: null,
      minute: min, second: sec,
      time: `00:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`,
      time_number: min >= halfTime ? 2 : 1,
      assist_player_id: null, assist_player_name: null,
      isSystem: true,
    }]);
  };

  // ═══ API helpers with auth ═══
  const getAuthHeaders = async () => {
    const isExpired = checkIfTokenExpired();
    if (isExpired) {
      await store.dispatch(refreshTokens());
    }
    const tokens = getTokensFromStore();
    return {
      'Authorization': 'Bearer ' + (tokens ? tokens.access_token : ''),
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
  };

  // ═══ API: Change Match State ═══
  const callChangeState = async (condition) => {
    if (!matchData?.id) return;
    try {
      const headers = await getAuthHeaders();
      await axios.post(BASE_URL.url + '/matches/changeState', {
        match_id: matchData.id,
        condition: condition,
        team_id: homeTeamId,
      }, { headers });
    } catch (err) {
      console.error('changeState error:', err);
      showNotif(t('olmErrState'));
    }
  };

  // ═══ API: Set Event ═══
  // Тот же рут, что у страницы матча: time — время на табло как есть.
  // Старый /matches/setEvent во 2-м тайме прибавлял длину тайма (35' → 65').
  const callSetEvent = async (evtData) => {
    if (!matchData?.id) return null;
    try {
      const headers = await getAuthHeaders();
      const clock = toEventTime(
        evtData.minute,
        evtData.second,
        halfTime,
        extraHalfMin,
      );
      const res = await axios.post(BASE_URL.url + API_URLS.addAdminMatchEvent, {
        match_id: matchData.id,
        event_id: evtData.event_type,
        player_id: evtData.player_id,
        team_id: evtData.team_id,
        league_id: matchData.league_id,
        time: clock.time,
        additional_player_id: evtData.additional_player_id || (evtData.assist_player_id || null),
        // Тайм по этапу матча (из формы); по минуте — только если этап неизвестен.
        time_number: evtData.time_number || clock.time_number,
        comment: evtData.comment || null,
        note: evtData.comment || null,
      }, { headers });
      return res.data;
    } catch (err) {
      console.error('setEvent error:', err);
      showNotif(t('olmErrEvent'));
      return null;
    }
  };

  // ═══ API: Remove Event ═══
  const callRemoveEvent = async (evt) => {
    if (!matchData?.id || !evt.server_id) {
      console.warn('removeEvent skipped: no server_id, event was not synced. Event:', evt);
      showNotif(t('olmErrNoServerId'));
      return false;
    }
    try {
      const headers = await getAuthHeaders();
      await axios.post(BASE_URL.url + '/matches/removeEvent', {
        match_id: matchData.id,
        event_id: evt.server_id,
        event_type: evt.event_type,
        team_id: evt.team_id,
        additional_event_id: evt.additional_event_id || null,
      }, { headers });
      return true;
    } catch (err) {
      console.error('removeEvent error:', err);
      showNotif(t('olmErrRemove'));
      return false;
    }
  };

  // ═══ API: Change Event ═══
  const callChangeEvent = async (evtData, previousEventType, previousTeamId) => {
    if (!matchData?.id || !evtData.server_id) {
      console.warn('changeEvent skipped: no server_id, event was not synced');
      return;
    }
    try {
      const headers = await getAuthHeaders();
      const clock = toEventTime(
        evtData.minute,
        evtData.second,
        halfTime,
        extraHalfMin,
      );
      await axios.post(BASE_URL.url + '/matches/changeEvent', {
        match_id: matchData.id,
        event_id: evtData.server_id,
        event_type: evtData.event_type,
        team_id: evtData.team_id,
        player_id: evtData.player_id,
        league_id: matchData.league_id,
        time: clock.time,
        additional_player_id: evtData.additional_player_id || (evtData.assist_player_id || null),
        time_number: evtData.time_number || clock.time_number,
        previous_event_type: previousEventType,
        previous_team_id: previousTeamId,
        additional_event_id: evtData.additional_event_id || null,
        comment: evtData.comment || null,
        note: evtData.comment || null,
      }, { headers });
    } catch (err) {
      console.error('changeEvent error:', err);
      showNotif(t('olmErrChange'));
    }
  };

  // ═══ API: Fetch Events from Server ═══
  const fetchServerEvents = async () => {
    if (!matchData?.id) return [];
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(BASE_URL.url + '/matches/' + matchData.id + '/events', { headers });
      const raw = res.data?.data || res.data || [];
      return Array.isArray(raw) ? raw : [];
    } catch (err) {
      console.error('fetchServerEvents error:', err);
      return [];
    }
  };

  // ═══ API: Fetch Match Data from Server (to get status) ═══
  const fetchMatchFromServer = async () => {
    if (!matchData?.id) return null;
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(BASE_URL.url + '/matches/' + matchData.id, { headers });
      return res.data?.data || res.data || null;
    } catch (err) {
      console.error('fetchMatchFromServer error:', err);
      return null;
    }
  };

  // ═══ Convert server event to local format ═══
  const serverEventToLocal = (sEvt) => {
    const player = matchApplication ? matchApplication.find(p => p.id === sEvt.player_id) : null;
    const addPlayer = sEvt.additional_player_id ? (matchApplication ? matchApplication.find(p => p.id === sEvt.additional_player_id) : null) : null;
    // Parse time "HH:MM:SS" or "MM:SS"
    let minute = 0, second = 0;
    if (sEvt.time) {
      const parts = sEvt.time.split(':').map(Number);
      if (parts.length === 3) {
        minute = (parts[0] > 0 ? parts[0] * 60 : 0) + parts[1];
        second = parts[2];
      } else if (parts.length === 2) {
        minute = parts[0];
        second = parts[1];
      }
    }
    const eventType = sEvt.event_id; // In DB: event_id = event type code
    const isSystem = SYSTEM_EVENT_IDS.includes(eventType);
    const timeNumber = sEvt.time_number || (minute >= halfTime ? 2 : 1);
    minute = absoluteEventMinute(
      sEvt.time || `00:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`,
      timeNumber,
      halfTime,
      extraHalfMin,
    );
    return {
      id: sEvt.id, // Use server ID as the local ID too
      server_id: sEvt.id, // Server DB record ID
      event_type: eventType,
      player_id: sEvt.player_id,
      player_name: player ? (player.family + ' ' + player.name) : (sEvt.player_family ? sEvt.player_family + ' ' + (sEvt.player_name || '') : null),
      player_number: player ? player.player_number : (sEvt.player_number || null),
      team_id: sEvt.team_id,
      team_name: sEvt.team_id === homeTeamId ? matchData?.home_team_name : (sEvt.team_id === guestTeamId ? matchData?.guest_team_name : null),
      minute, second,
      time: sEvt.time || `00:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`,
      time_number: timeNumber,
      assist_player_id: (eventType === 1 && sEvt.additional_player_id) ? sEvt.additional_player_id : null,
      assist_player_name: (eventType === 1 && addPlayer) ? (addPlayer.family + ' ' + addPlayer.name) : null,
      additional_player_id: (eventType === 15 && sEvt.additional_player_id) ? sEvt.additional_player_id : null,
      additional_player_name: (eventType === 15 && addPlayer) ? (addPlayer.family + ' ' + addPlayer.name) : null,
      additional_event_id: sEvt.additional_event_id || null,
      comment: sEvt.comment || sEvt.note || '',
      isSystem,
    };
  };

  // ═══ Reload events from server and sync local state ═══
  const syncEventsFromServer = async () => {
    const serverEvents = await fetchServerEvents();
    // Filter out assist events (event_id=2) - they are linked to goals
    const processed = [];
    serverEvents.forEach((sEvt) => {
      if (sEvt.event_id === 2) {
        // Link assist to previous event (goal)
        if (processed.length > 0) {
          processed[processed.length - 1].additional_event_id = sEvt.id;
        }
      } else {
        processed.push(serverEventToLocal(sEvt));
      }
    });
    setMatchEvents(processed);
  };

  // ═══ Restore match state from server ═══
  // Замены поверх стартового состава; уже применённые пропускаются.
  const applySubs = (lineup, teamId, events) => {
    const next = { ...lineup };
    events
      .filter(e => e.event_type === 15 && Number(e.team_id) === Number(teamId) && !e.isSystem && e.additional_player_id)
      .sort((a, b) => a.minute - b.minute || a.second - b.second)
      .forEach((sub) => {
        const slotKey = Object.keys(next).find(k => Number(next[k]) === Number(sub.player_id));
        const alreadyIn = Object.values(next).some(id => Number(id) === Number(sub.additional_player_id));
        if (slotKey !== undefined && !alreadyIn) next[slotKey] = sub.additional_player_id;
      });
    return next;
  };

  // Таймер по меткам сервера — когда своего сохранённого нет (другое устройство, чистый браузер).
  const clockFromServer = (serverMatch, playPhase) => {
    const { extraStartAt, extraSecondAt, extraStart, extraSecond } = matchExtraMarkers(serverMatch);
    const fullTime = halfTime * 2 * 60;
    switch (playPhase) {
      case 'finished':
      case 'extra_ended':
      case 'tech_defeat':
        if (extraSecond) return makeClock('ended', fullTime + extraHalfSec * 2);
        if (extraStart) return makeClock('ended', fullTime + extraHalfSec);
        return makeClock('ended', fullTime);
      case 'extra_timeout':
        return makeClock('extra_halftime', fullTime + extraHalfSec);
      case 'extra_break':
        return makeClock('extra_break', fullTime);
      case 'half_time':
        return makeClock('halftime', halfTime * 60);
      case 'extra_second':
        return makeClock('extra2', fullTime + extraHalfSec + elapsedSecondsSince(extraSecondAt));
      case 'extra_first':
        return makeClock('extra1', fullTime + elapsedSecondsSince(extraStartAt));
      case 'second_half':
        return makeClock('half2', halfTime * 60 + elapsedSecondsSince(serverMatch.second_time_start));
      default:
        return makeClock('half1', serverMatch.start_in ? elapsedSecondsSince(serverMatch.start_in) : 0);
    }
  };

  const restoreMatchFromServer = async () => {
    setIsRestoring(true);
    try {
      const serverMatch = await fetchMatchFromServer();
      if (!serverMatch) { setIsRestoring(false); return false; }

      // not_started — матч не начат; перерыв (status null + start_in) — это уже идущий матч.
      const playPhase = matchPlayPhase(serverMatch);
      if (playPhase === 'not_started' || playPhase === 'postponed') { setIsRestoring(false); return false; }

      // Match is in progress or ended — restore state
      // Load events
      const serverEvents = await fetchServerEvents();
      const processedEvents = [];
      serverEvents.forEach((sEvt) => {
        if (sEvt.event_id === 2) {
          if (processedEvents.length > 0) {
            processedEvents[processedEvents.length - 1].additional_event_id = sEvt.id;
          }
        } else {
          processedEvents.push(serverEventToLocal(sEvt));
        }
      });
      setMatchEvents(processedEvents);

      const stored = loadOnlineMatch(matchData.id);
      const storedSetup = stored?.setup;
      const sessionSetup = loadFromSession();
      const sessionSame = sessionSetup && Number(sessionSetup.matchId) === Number(matchData.id) ? sessionSetup : null;
      const colorsFrom = storedSetup || sessionSame || {};
      setHomeColor(colorsFrom.homeColor || '#3498db');
      setGuestColor(colorsFrom.guestColor || '#e74c3c');

      if (matchApplication) {
        const sel = {};
        matchApplication.forEach(p => { sel[p.id] = !!p.active; });
        const serverSetup = setupFromServer(serverMatch.lineups || matchData.lineups, homeTeamId, guestTeamId);

        if (isLineupSetupComplete(storedSetup)) {
          // Состав и расстановка, отмеченные в онлайне (с заменами и перестановками).
          setSelectedPlayers(storedSetup.selectedPlayers || sel);
          setMatchFormat(storedSetup.matchFormat);
          setHomeFormation(storedSetup.homeFormation);
          setGuestFormation(storedSetup.guestFormation);
          setHomeLineup(applySubs(storedSetup.homeLineup, homeTeamId, processedEvents));
          setGuestLineup(applySubs(storedSetup.guestLineup, guestTeamId, processedEvents));
        } else if (isLineupSetupComplete(serverSetup)) {
          // Расстановка, сохранённая на сервере.
          setSelectedPlayers(sel);
          setMatchFormat(serverSetup.matchFormat);
          setHomeFormation(serverSetup.homeFormation);
          setGuestFormation(serverSetup.guestFormation);
          setHomeLineup(applySubs(serverSetup.homeLineup, homeTeamId, processedEvents));
          setGuestLineup(applySubs(serverSetup.guestLineup, guestTeamId, processedEvents));
        } else {
          setSelectedPlayers(sel);

          // Build lineups from active players
          const homePlayers = matchApplication.filter(p => (p.team_id === homeTeamId || p.isDouble === homeTeamId) && p.active);
          const guestPlayers = matchApplication.filter(p => (p.team_id === guestTeamId || p.isDouble === guestTeamId) && p.active);

          // Find keepers
          const homeKeeper = homePlayers.find(p => p.keeper);
          const guestKeeper = guestPlayers.find(p => p.keeper);

          // Build lineup objects: slot 0 = keeper, rest = field players
          const buildLineup = (players, keeper) => {
            const lineup = {};
            let slot = 0;
            // Keeper first (slot 0)
            if (keeper) {
              lineup[slot] = keeper.id;
              slot++;
            }
            // Other active players
            players.forEach(p => {
              if (keeper && p.id === keeper.id) return;
              lineup[slot] = p.id;
              slot++;
            });
            return lineup;
          };

          const hLineup = buildLineup(homePlayers, homeKeeper);
          const gLineup = buildLineup(guestPlayers, guestKeeper);

          setHomeLineup(applySubs(hLineup, homeTeamId, processedEvents));
          setGuestLineup(applySubs(gLineup, guestTeamId, processedEvents));

          // Auto-detect format from number of active players per team
          const maxActive = Math.max(Object.keys(hLineup).length, Object.keys(gLineup).length);
          let detectedFormat = '';
          for (const fmt of MATCH_FORMATS) {
            const num = parseInt(fmt.split('x')[0]);
            if (num === maxActive) { detectedFormat = fmt; break; }
          }
          if (detectedFormat) {
            setMatchFormat(detectedFormat);
            const formations = FORMATIONS_BY_FORMAT[detectedFormat] || [];
            if (formations.length > 0) {
              setHomeFormation(formations[0]);
              setGuestFormation(formations[0]);
            }
          }
        }
      }

      // Таймер: общий с сервера (паузы с других устройств и пульта табло), иначе свой сохранённый,
      // если он про ту же фазу матча, иначе — по меткам начала таймов.
      const adopted = adoptServerClock(matchData.id, serverMatch);
      if (!adopted && !clockMatchesServer(loadOnlineMatch(matchData.id)?.clock, serverMatch)) {
        setClock(clockFromServer(serverMatch, playPhase), { sync: false });
      }
      const ended = ['finished', 'extra_ended', 'tech_defeat'].includes(playPhase);
      setPhase(ended ? 'ended' : 'live');

      setIsRestoring(false);
      return true;
    } catch (err) {
      console.error('restoreMatchFromServer error:', err);
      setIsRestoring(false);
      return false;
    }
  };

  // ═══ API: Set Application (lineup) ═══
  const callSetApplication = async (teamId) => {
    if (!matchData?.id) return;
    try {
      const headers = await getAuthHeaders();
      const teamPlayers = getTeamPlayers(teamId);
      const activeIds = teamPlayers.filter(p => selectedPlayers[p.id]).map(p => p.id);
      const inActiveIds = teamPlayers.filter(p => !selectedPlayers[p.id]).map(p => p.id);
      const doublesIds = teamPlayers.filter(p => p.isDouble).map(p => p.id);
      // Find goalkeeper: slot 0 in the corresponding lineup
      const lineup = teamId === homeTeamId ? homeLineup : guestLineup;
      const keeperId = lineup[0] || false;
      await axios.post(BASE_URL.url + '/matches/setApplication', {
        match_id: matchData.id,
        team_id: teamId,
        activeIds,
        inActiveIds,
        doublesIds,
        keeperId,
        matchNumbers: [],
      }, { headers });
    } catch (err) {
      console.error('setApplication error:', err);
      showNotif(t('olmErrApp'));
    }
  };

  // ═══ API: Save formation + slots (как блок «Расстановка» на странице матча) ═══
  const callSetLineup = async () => {
    if (!matchData?.id || !isLineupSetupComplete(setupValue)) return;
    try {
      const headers = await getAuthHeaders();
      await axios.post(BASE_URL.url + API_URLS.setAdminMatchLineup, {
        match_id: matchData.id,
        match_format: matchFormat,
        lineups: [
          { team_id: homeTeamId, formation: homeFormation, assignments: lineupToAssignments(homeFormation, homeLineup) },
          { team_id: guestTeamId, formation: guestFormation, assignments: lineupToAssignments(guestFormation, guestLineup) },
        ],
      }, { headers });
    } catch (err) {
      // Не критично: расстановка всё равно сохранена локально.
      console.error('setAdminMatchLineup error:', err);
    }
  };

  const startFirstHalf = async () => {
    showNotif(t('olmSavingApps'));
    await callSetApplication(homeTeamId);
    await callSetApplication(guestTeamId);
    await callSetLineup();
    showNotif(t('olmStarting'));
    await callChangeState('start');
    setClock(makeClock('half1', 0));
    addSystemEvent(9, 0);
    showNotif(t('olmMatchStarted'));
  };

  const pauseTimer = () => setClock(pauseClock);

  const resumeTimer = () => setClock(resumeClock);

  // Пробел — пауза/продолжить (в мини-футболе таймер останавливают на каждом ауте).
  const toggleTimerHotkey = useCallback(() => setClock(toggleClock), [setClock]);
  useClockHotkey(
    Boolean(visible) && phase === 'live' && !eventModalOpen && !editingTimer,
    toggleTimerHotkey,
  );

  const callHalftime = async () => {
    await callChangeState('timeout');
    addSystemEvent(10, displaySeconds);
    setClock(makeClock('halftime', displaySeconds));
    showNotif(t('olmHalfTimeNotif'));
  };

  const startSecondHalf = async () => {
    await callChangeState('continue');
    setClock(makeClock('half2', halfTime * 60));
    showNotif(t('olmHalf2Started'));
  };

  const goToExtraBreak = async () => {
    await callChangeState('timeout_before_extra');
    addSystemEvent(14, halfTime * 2 * 60);
    setClock(makeClock('extra_break', halfTime * 2 * 60));
    showNotif(t('extraBreak'));
  };

  const startExtraFirst = async () => {
    await callChangeState('extra_first_time');
    setClock(makeClock('extra1', halfTime * 2 * 60));
    showNotif(t('olmExtra1Started'));
  };

  const callExtraHalftime = async () => {
    await callChangeState('extra_time_timeout');
    addSystemEvent(10, halfTime * 2 * 60 + extraHalfSec);
    setClock(makeClock('extra_halftime', halfTime * 2 * 60 + extraHalfSec));
    showNotif(t('extraHalfTime'));
  };

  const startExtraSecond = async () => {
    await callChangeState('extra_second_time');
    setClock(makeClock('extra2', halfTime * 2 * 60 + extraHalfSec));
    showNotif(t('olmExtra2Started'));
  };

  const endMatch = async () => {
    const inExtra = ['extra1', 'paused_extra1', 'extra_halftime', 'extra2', 'paused_extra2', 'extra_break'].includes(matchStatus);
    await callChangeState(inExtra ? 'extra_time_end' : 'end');
    addSystemEvent(11, displaySeconds);
    setClock(makeClock('ended', displaySeconds));
    setPhase('ended');
    showNotif(t('olmMatchEndedNotif'));
  };

  // Время можно поправить в любой момент онлайна: на паузе, в перерыве и на ходу (таймер продолжит с нового значения).
  const canEditTimer = matchStatus !== 'ended';

  const startEditTimer = () => {
    if (!canEditTimer) return;
    setEditMin(String(currentMinute));
    setEditSec(String(currentSecond));
    setEditingTimer(true);
  };

  const applyTimerEdit = () => {
    const m = Math.max(0, parseInt(editMin) || 0);
    const s = Math.min(59, Math.max(0, parseInt(editSec) || 0));
    setClock((current) => setClockSeconds(current || clock, m * 60 + s));
    setEditingTimer(false);
  };

  // ═══ EVENT RECORDING ═══
  const openEventModal = (teamSide, presetType, presetPlayerId) => {
    setEditingEventId(null);
    setEventModalTeamSide(teamSide);
    setEventType(presetType || 1);
    // Keep the player if provided
    setEventPlayer(presetPlayerId ? String(presetPlayerId) : '');
    setEventAssist('');
    setEventSubPlayer('');
    // Время и тайм — с таймера в момент открытия (на паузе — время остановки).
    const hint = clockEventHint(clock);
    setEventTimeHint(hint);
    setEventMinute(String(hint ? hint.minute : currentMinute));
    setEventSecond(String(hint ? hint.second : currentSecond));
    setEventComment('');
    setEventModalOpen(true);
  };

  const openEditEventModal = (evt) => {
    if (evt.isSystem) return;
    setEditingEventId(evt.id);
    const side = evt.team_id === homeTeamId ? 'home' : 'guest';
    setEventModalTeamSide(side);
    setEventType(evt.event_type);
    setEventPlayer(evt.player_id ? String(evt.player_id) : '');
    setEventAssist(evt.assist_player_id ? String(evt.assist_player_id) : '');
    setEventSubPlayer(evt.additional_player_id ? String(evt.additional_player_id) : '');
    setEventMinute(String(evt.minute));
    setEventSecond(String(evt.second));
    // Время не трогали — тайм события остаётся прежним.
    setEventTimeHint({ minute: evt.minute, second: evt.second, time_number: evt.time_number });
    setEventComment(evt.comment || '');
    setEventModalOpen(true);
  };

  const saveEvent = () => {
    const typeInfo = eventTypeById(eventType);
    const needsPlayer = Boolean(typeInfo?.needsPlayer);
    const needsSecond = Boolean(typeInfo?.needsSecond);
    const isSub = Number(eventType) === 15;
    if (needsPlayer && !eventPlayer) return;
    const teamId = eventModalTeamSide === 'home' ? homeTeamId : guestTeamId;
    const player = eventPlayer ? matchApplication.find(p => p.id === parseInt(eventPlayer)) : null;
    if (needsPlayer && !player) return;
    if (isSub && !eventSubPlayer) return;

    const min = parseInt(eventMinute) || 0;
    const sec = parseInt(eventSecond) || 0;
    const extraStartMin = halfTime * 2;
    const extraSecondMin = halfTime * 2 + extraHalfMin;
    // Тайм — по этапу матча на момент события; руками изменили время — считаем по минуте.
    const timeNumber = hintedTimeNumber(eventTimeHint, min, sec)
      || (min >= extraSecondMin ? 4 : min >= extraStartMin ? 3 : min > halfTime ? 2 : 1);

    const finalEventType = resolveYellowCardEventId(
      eventType,
      matchEvents.map((e) => ({ ...e, event_id: e.event_type })),
      player?.id,
      { isEdit: Boolean(editingEventId) },
    );

    const evtData = {
      id: editingEventId || (Date.now() + Math.random()),
      event_type: finalEventType,
      player_id: player?.id || null,
      player_name: player ? player.family + ' ' + player.name : '',
      player_number: player?.player_number || '',
      team_id: teamId,
      team_name: eventModalTeamSide === 'home' ? matchData.home_team_name : matchData.guest_team_name,
      minute: min, second: sec,
      time: `00:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`,
      time_number: timeNumber,
      assist_player_id: null, assist_player_name: null,
      additional_player_id: null, additional_player_name: null,
      comment: String(eventComment || '').trim(),
      isSystem: false,
    };

    if (needsSecond && !isSub && eventAssist) {
      const ap = matchApplication.find(p => p.id === parseInt(eventAssist));
      if (ap) {
        evtData.assist_player_id = ap.id;
        evtData.assist_player_name = ap.family + ' ' + ap.name;
      }
    }

    if (isSub && eventSubPlayer) {
      const sp = matchApplication.find(p => p.id === parseInt(eventSubPlayer));
      if (sp) {
        evtData.additional_player_id = sp.id;
        evtData.additional_player_name = sp.family + ' ' + sp.name;
      }
    }

    if (editingEventId) {
      const oldEvt = matchEvents.find(e => e.id === editingEventId);
      if (oldEvt?.server_id) evtData.server_id = oldEvt.server_id;
      if (oldEvt?.additional_event_id) evtData.additional_event_id = oldEvt.additional_event_id;
      setMatchEvents(prev => prev.map(e => e.id === editingEventId ? evtData : e));
      showNotif(t('olmEventUpdated'));
      callChangeEvent(evtData, oldEvt?.event_type, oldEvt?.team_id).then(() => {
        syncEventsFromServer();
      });
    } else {
      setMatchEvents(prev => [...prev, evtData]);
      const name = player ? player.family + ' ' + player.name : '';
      const msg = finalEventType === SECOND_YELLOW_CARD_EVENT_ID && player
        ? t('olm2YC', { name })
        : t('olmEventNotif', { icon: '', label: eventLabel(finalEventType, t), name });
      showNotif(msg);

      callSetEvent(evtData).then(() => {
        syncEventsFromServer();
      });

      if (isSub && eventSubPlayer) {
        const side = eventModalTeamSide;
        const lineup = side === 'home' ? { ...homeLineup } : { ...guestLineup };
        const slotKey = Object.keys(lineup).find(k => lineup[k] === player.id);
        if (slotKey !== undefined) {
          lineup[slotKey] = parseInt(eventSubPlayer);
          if (side === 'home') setHomeLineup(lineup);
          else setGuestLineup(lineup);
        }
      }
    }

    setEventModalOpen(false);
    setEditingEventId(null);
  };

  const removeEvent = async (eventId) => {
    const evt = matchEvents.find(e => e.id === eventId);
    if (!evt || evt.isSystem) return;
    // Remove from UI immediately (optimistic)
    setMatchEvents(prev => prev.filter(e => e.id !== eventId));
    // Remove from server
    const success = await callRemoveEvent(evt);
    if (success) {
      // Reload events from server to stay in sync
      await syncEventsFromServer();
    } else {
      // Restore event in UI if server deletion failed
      setMatchEvents(prev => [...prev, evt]);
    }
  };

  const handleFieldPlayerClick = (side, playerId) => {
    if (!isMatchActive()) return;
    if (isPlayerSubbedOut(playerId)) {
      showNotif(t('olmAlreadySubbed'));
      return;
    }
    openEventModal(side, isPlayerBanned(playerId) ? 15 : 1, playerId);
  };

  const getPlayerCardIndicators = (playerId) => {
    const events = matchEvents.filter(e => e.player_id === playerId && !e.isSystem);
    let yellow = 0, red = 0, goals = 0;
    events.forEach(e => {
      if (e.event_type === 3) yellow++;
      if (e.event_type === 13) { yellow++; red++; } // 2nd yellow = YC + RC
      if (e.event_type === 4) red++;
      if ([1, 6, 16, 18].includes(e.event_type)) goals++;
    });
    return { yellow, red, goals, banned: red > 0 };
  };

  const handleClose = () => closeModal();

  // ═══════════════════════════════════════
  // RENDER: SETUP PHASES (roster + lineup)
  // ═══════════════════════════════════════

  const renderSetupPhase = () => (
    <MatchLineupSetup
      step={phase}
      onStepChange={setPhase}
      matchData={matchData}
      matchApplication={matchApplication}
      value={setupValue}
      onChange={applySetupPatch}
      onCancel={handleClose}
      onSubmit={() => setPhase('live')}
      submitLabel={t('olmStartMatch')}
      notify={showNotif}
    />
  );

  // ═══════════════════════════════════════
  // RENDER: LIVE PHASE
  // ═══════════════════════════════════════

  const renderTimerControls = () => {
    switch (matchStatus) {
      case 'idle':
        return <div className="olm-btn olm-btn-start" onClick={startFirstHalf}>{t('olmStartHalf1')}</div>;
      case 'half1':
        return (
          <>
            <div className="olm-btn olm-btn-warning" onClick={pauseTimer}>{t('olmPause')}</div>
            <div className="olm-btn olm-btn-primary" onClick={callHalftime}>{t('olmHalftime')}</div>
          </>
        );
      case 'paused1':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={resumeTimer}>{t('olmResume')}</div>
            <div className="olm-btn olm-btn-primary" onClick={callHalftime}>{t('olmHalftime')}</div>
          </>
        );
      case 'halftime':
        return <div className="olm-btn olm-btn-start" onClick={startSecondHalf}>{t('olmStartHalf2')}</div>;
      case 'half2':
        return (
          <>
            <div className="olm-btn olm-btn-warning" onClick={pauseTimer}>{t('olmPause')}</div>
            {extraTied ? (
              <div className="olm-btn olm-btn-start" onClick={goToExtraBreak}>{t('olmStartExtra')}</div>
            ) : null}
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>{t('olmEndMatch')}</div>
          </>
        );
      case 'paused2':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={resumeTimer}>{t('olmResume')}</div>
            {extraTied ? (
              <div className="olm-btn olm-btn-start" onClick={goToExtraBreak}>{t('olmStartExtra')}</div>
            ) : null}
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>{t('olmEndMatch')}</div>
          </>
        );
      case 'extra_break':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={startExtraFirst}>{t('olmStartExtra1')}</div>
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>{t('olmEndMatch')}</div>
          </>
        );
      case 'extra1':
        return (
          <>
            <div className="olm-btn olm-btn-warning" onClick={pauseTimer}>{t('olmPause')}</div>
            <div className="olm-btn olm-btn-primary" onClick={callExtraHalftime}>{t('olmExtraHalftime')}</div>
          </>
        );
      case 'paused_extra1':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={resumeTimer}>{t('olmResume')}</div>
            <div className="olm-btn olm-btn-primary" onClick={callExtraHalftime}>{t('olmExtraHalftime')}</div>
          </>
        );
      case 'extra_halftime':
        return <div className="olm-btn olm-btn-start" onClick={startExtraSecond}>{t('olmStartExtra2')}</div>;
      case 'extra2':
        return (
          <>
            <div className="olm-btn olm-btn-warning" onClick={pauseTimer}>{t('olmPause')}</div>
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>{t('olmEndMatch')}</div>
          </>
        );
      case 'paused_extra2':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={resumeTimer}>{t('olmResume')}</div>
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>{t('olmEndMatch')}</div>
          </>
        );
      default:
        return null;
    }
  };

  const renderEventButtons = (teamSide) => {
    if (!isMatchActive()) return null;
    return (
      <div className="olm-field-event-buttons">
        <button
          type="button"
          className="olm-btn olm-btn-event-sm"
          onClick={() => openEventModal(teamSide)}
        >
          {t('olmAddEvent')}
        </button>
      </div>
    );
  };

  const renderLiveField = (side) => {
    const formation = side === 'home' ? homeFormation : guestFormation;
    const lineup = side === 'home' ? homeLineup : guestLineup;
    const teamName = side === 'home' ? matchData.home_team_name : matchData.guest_team_name;
    const teamColor = side === 'home' ? homeColor : guestColor;
    const bench = getLiveBenchPlayers(side);

    if (!formation) return null;
    const positions = generateFieldPositions(formation);

    const handleDragOver = (e) => e.preventDefault();

    const handleDropOnSlot = (slotIndex) => {
      if (!draggingPlayer) return;
      const dragId = draggingPlayer.id;
      const dragFromSide = draggingPlayer.fromSide;
      if (dragFromSide !== side) return;
      const currentLineup = { ...(side === 'home' ? homeLineup : guestLineup) };
      const existingPlayerId = currentLineup[slotIndex];

      if (existingPlayerId && existingPlayerId !== dragId) {
        // Dragging bench player onto occupied slot = substitution
        if (draggingPlayer.fromBench) {
          // Create substitution event: existingPlayer OUT, dragId IN
          const outPlayer = findPlayerById(existingPlayerId);
          const inPlayer = findPlayerById(dragId);
          if (outPlayer && inPlayer && isMatchActive()) {
            const tId = side === 'home' ? homeTeamId : guestTeamId;
            const min = currentMinute;
            const sec = currentSecond;
            const subEvent = {
              id: Date.now() + Math.random(),
              event_type: 15,
              player_id: outPlayer.id,
              player_name: outPlayer.family + ' ' + outPlayer.name,
              player_number: outPlayer.player_number,
              team_id: tId,
              team_name: side === 'home' ? matchData.home_team_name : matchData.guest_team_name,
              minute: min, second: sec,
              time: `00:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`,
              time_number: timeNumberForStatus(matchStatus) || (min > halfTime ? 2 : 1),
              assist_player_id: null, assist_player_name: null,
              additional_player_id: inPlayer.id,
              additional_player_name: inPlayer.family + ' ' + inPlayer.name,
              comment: '',
              isSystem: false,
            };
            setMatchEvents(prev => [...prev, subEvent]);
            currentLineup[slotIndex] = dragId;
            if (side === 'home') setHomeLineup(currentLineup);
            else setGuestLineup(currentLineup);
            showNotif(t('olmSubNotif', { out: outPlayer.family, inName: inPlayer.family }));
            // Send substitution to server, then reload events
            callSetEvent(subEvent).then(() => {
              syncEventsFromServer();
            });
          }
        }
      } else if (!existingPlayerId) {
        // Drag onto empty slot
        currentLineup[slotIndex] = dragId;
        // Remove from old slot if moving
        if (!draggingPlayer.fromBench) {
          Object.keys(currentLineup).forEach(k => {
            if (parseInt(k) !== slotIndex && currentLineup[k] === dragId) delete currentLineup[k];
          });
        }
        if (side === 'home') setHomeLineup(currentLineup);
        else setGuestLineup(currentLineup);
      }
      setDraggingPlayer(null);
    };

    return (
      <div className="olm-live-field-wrapper">
        <div className="olm-live-field-header">{teamName}</div>
        <div className={`olm-field olm-field-live ${fieldDensityClass(formation)}`} onDragOver={handleDragOver}>
          <div className="olm-field-half-line" />
          <div className="olm-field-center-circle" />
          <div className="olm-field-penalty-area olm-field-penalty-bottom" />
          <div className="olm-field-penalty-area olm-field-penalty-top" />
          {positions.map((pos, i) => {
            const playerId = lineup[i];
            const player = playerId ? findPlayerById(playerId) : null;
            const indicators = playerId ? getPlayerCardIndicators(playerId) : { yellow: 0, red: 0, goals: 0, banned: false };
            const subbedOut = playerId && isPlayerSubbedOut(playerId);
            const redCarded = indicators.banned;
            const inactive = subbedOut;
            const clickable = isMatchActive() && player && !subbedOut;

            return (
              <div
                key={i}
                className={`olm-field-slot ${player ? 'olm-field-slot-filled' : 'olm-field-slot-empty'} ${clickable ? 'olm-field-slot-clickable' : ''} ${inactive ? 'olm-field-slot-banned' : ''} ${redCarded && !subbedOut ? 'olm-field-slot-red-carded' : ''}`}
                style={{
                  top: `${pos.top}%`, left: `${pos.left}%`,
                  background: player ? (inactive ? '#95a5a6' : redCarded ? '#c0392b' : (i === 0 ? '#f39c12' : teamColor)) : 'transparent',
                  cursor: clickable ? 'pointer' : 'default',
                  opacity: inactive ? 0.4 : 1,
                }}
                draggable={!!player && !inactive}
                onDragStart={() => player && !inactive && setDraggingPlayer({ id: player.id, fromSide: side, fromBench: false })}
                onDragOver={handleDragOver}
                onDrop={() => handleDropOnSlot(i)}
                onClick={() => { if (clickable) handleFieldPlayerClick(side, player.id); }}
              >
                {player ? (
                  <>
                    <span className="olm-field-player-pos">{getPositionShort(player, i)}</span>
                    <span className="olm-field-player-number">{player.player_number || ''}</span>
                    <span className={`olm-field-player-name ${redCarded && !subbedOut ? 'olm-field-player-name-banned' : ''}`}>{player.family}</span>
                    {indicators.yellow > 0 && <span className="olm-card-indicator olm-card-yellow">🟨</span>}
                    {indicators.red > 0 && <span className="olm-card-indicator olm-card-red">🟥</span>}
                    {indicators.goals > 0 && <span className="olm-goal-indicator">{'⚽'.repeat(indicators.goals)}</span>}
                  </>
                ) : (
                  <span className="olm-field-slot-label">{i === 0 ? 'GK' : ''}</span>
                )}
              </div>
            );
          })}
        </div>
        {renderEventButtons(side)}

        {/* Live bench (for substitutions) */}
        {bench.length > 0 && (
          <div className="olm-bench olm-bench-live">
            <div className="olm-bench-title">{t('olmBench', { count: bench.length })}</div>
            <div className="olm-bench-list">
              {bench.map(p => (
                <div
                  key={p.id}
                  className="olm-player-badge"
                  style={{ background: teamColor, cursor: 'grab' }}
                  draggable
                  onDragStart={() => setDraggingPlayer({ id: p.id, fromSide: side, fromBench: true })}
                >
                  <span className="olm-player-badge-number">{p.player_number || '—'}</span>
                  <span className="olm-player-badge-name">{p.family}</span>
                  {p.position_id && <span className="olm-player-badge-pos">{t(POSITION_LABELS[p.position_id])}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLivePhase = () => (
    <div className="olm-live-phase">
      {/* Timer + Scoreboard */}
      <div className="olm-match-control">
        <div className="olm-timer-row">
          <div className="olm-timer-display">
            <span className="olm-timer-status">{statusLabel()}</span>
            {editingTimer ? (
              <div className="olm-timer-edit-row">
                <input
                  type="number" className="olm-timer-edit-input" value={editMin}
                  onChange={e => setEditMin(e.target.value)} min="0"
                  onKeyDown={e => { if (e.key === 'Enter') applyTimerEdit(); }}
                  autoFocus
                />
                <span className="olm-timer-time" style={{ fontSize: 32 }}>:</span>
                <input
                  type="number" className="olm-timer-edit-input" value={editSec}
                  onChange={e => setEditSec(e.target.value)} min="0" max="59"
                  onKeyDown={e => { if (e.key === 'Enter') applyTimerEdit(); }}
                />
                <div className="olm-btn olm-btn-primary" style={{ padding: '6px 14px', fontSize: 13, marginLeft: 8 }} onClick={applyTimerEdit}>OK</div>
                <div className="olm-btn olm-btn-secondary" style={{ padding: '6px 14px', fontSize: 13, marginLeft: 4 }} onClick={() => setEditingTimer(false)}>✕</div>
              </div>
            ) : (
              <span
                className="olm-timer-time"
                onClick={startEditTimer}
                title={canEditTimer ? t('olmEditTimer') : undefined}
                style={{ cursor: canEditTimer ? 'pointer' : 'default' }}
              >
                {formatTime(displaySeconds)}
                {canEditTimer ? <span className="olm-timer-edit-hint" aria-hidden="true">✎</span> : null}
              </span>
            )}
          </div>
        </div>
        {/* Compact scoreboard */}
        <div className="olm-scoreboard-inline">
          <img src={teamLogo(matchData.home_team_image)} alt="" className="olm-scoreboard-logo-sm" />
          <span className="olm-scoreboard-name-sm">{matchData.home_team_name}</span>
          <span className="olm-score-inline">{score.home} : {score.guest}</span>
          <span className="olm-scoreboard-name-sm">{matchData.guest_team_name}</span>
          <img src={teamLogo(matchData.guest_team_image)} alt="" className="olm-scoreboard-logo-sm" />
        </div>
        <div className="olm-control-buttons">
          {renderTimerControls()}
          {matchStatus === 'idle' && (
            <div className="olm-btn olm-btn-secondary" onClick={() => setPhase('lineup')}>{t('olmEditLineup')}</div>
          )}
        </div>
        {['half1', 'paused1', 'half2', 'paused2', 'extra1', 'paused_extra1', 'extra2', 'paused_extra2'].includes(matchStatus) ? (
          <div className="olm-hotkey-hint">{t('olmSpaceHint')}</div>
        ) : null}
        {/* Jersey color pickers */}
        <div className="olm-color-pickers olm-color-pickers-live">
          <div className="olm-color-picker-item">
            <label>{matchData.home_team_name}:</label>
            <input type="color" value={homeColor} onChange={e => setHomeColor(e.target.value)} />
          </div>
          <div className="olm-color-picker-item">
            <label>{matchData.guest_team_name}:</label>
            <input type="color" value={guestColor} onChange={e => setGuestColor(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Two fields side by side */}
      <div className="olm-live-fields-row">
        {renderLiveField('home')}
        {renderLiveField('guest')}
      </div>

      {/* Event History */}
      <div className="olm-event-history" style={{ maxWidth: '100%' }}>
        <div className="olm-event-history-title">{t('olmMatchEvents', { count: matchEvents.filter(e => !e.isSystem).length })}</div>
        {matchEvents.length === 0 ? (
          <div className="olm-event-empty">{t('olmNoEvents')}</div>
        ) : (
          <div className="olm-event-list">
            {[...matchEvents].sort((a, b) => a.minute - b.minute || a.second - b.second).map(e => {
              const sysLabel = SYSTEM_EVENT_IDS.includes(Number(e.event_type));
              return (
                <div
                  key={e.id}
                  className={`olm-event-item ${e.team_id === homeTeamId ? 'olm-event-home' : e.team_id === guestTeamId ? 'olm-event-guest' : ''} ${!e.isSystem ? 'olm-event-item-editable' : ''}`}
                  onClick={() => { if (!e.isSystem) openEditEventModal(e); }}
                >
                  <span className="olm-event-time">{formatEventMinuteLabel(e.minute, e.second)}'</span>
                  <div className="olm-event-info">
                    {sysLabel ? (
                      <span className="olm-event-label">{eventLabel(e.event_type, t)}</span>
                    ) : (
                      <>
                        <span className="olm-event-label">{eventLabel(e.event_type, t)}</span>
                        <span className="olm-event-player">{e.player_name}</span>
                        {e.assist_player_name && <span className="olm-event-assist">{t('olmPass', { name: e.assist_player_name })}</span>}
                        {e.event_type === 15 && e.additional_player_name && <span className="olm-event-assist">➜ {e.additional_player_name}</span>}
                        {e.comment ? <span className="olm-event-note">{e.comment}</span> : null}
                        <span className="olm-event-team">{e.team_name}</span>
                      </>
                    )}
                  </div>
                  {!e.isSystem && (
                    <span className="olm-event-remove" title={t('olmRemove')} onClick={(ev) => { ev.stopPropagation(); removeEvent(e.id); }}>✕</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════
  // RENDER: ENDED PHASE
  // ═══════════════════════════════════════

  const renderEndedPhase = () => {
    const playerStats = collectPlayerStats(matchEvents);

    return (
      <div className="olm-phase-ended">
        <div className="olm-ended-banner">
          <h2>{t('olmMatchEnded')}</h2>
          <div className="olm-ended-score">
            {matchData.home_team_name} {score.home} : {score.guest} {matchData.guest_team_name}
          </div>
        </div>

        {/* Player stats summary */}
        <div className="olm-roster-columns" style={{ marginBottom: 16 }}>
          {[homeTeamId, guestTeamId].map(teamId => {
            const teamName = teamId === homeTeamId ? matchData.home_team_name : matchData.guest_team_name;
            const players = getSelectedTeamPlayers(teamId);
            return (
              <div key={teamId} className="olm-roster-team" style={{ maxHeight: 'none' }}>
                <div className="olm-roster-team-name" style={{ marginBottom: 8, fontWeight: 700 }}>{teamName}</div>
                <table className="olm-ended-table">
                  <thead>
                    <tr>
                      <th>{t('player')}</th>
                      <th style={{ width: 30, textAlign: 'center' }}>⚽</th>
                      <th style={{ width: 30, textAlign: 'center' }}>🅰️</th>
                      <th style={{ width: 30, textAlign: 'center' }}>{t('mrOG')}</th>
                      <th style={{ width: 30, textAlign: 'center' }}>🟨</th>
                      <th style={{ width: 30, textAlign: 'center' }}>🟥</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(p => {
                      const s = playerStats[p.id] || {};
                      const hasStats = (s.goals || s.assists || s.autogoals || s.yellow_cards || s.red_cards);
                      return (
                        <tr key={p.id} className={hasStats ? 'olm-ended-table-row-stats' : ''}>
                          <td>{p.player_number} {p.family} {p.name}</td>
                          <td style={{ textAlign: 'center' }}>{s.goals || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.assists || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.autogoals || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.yellow_cards || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.red_cards || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Event log */}
        <div className="olm-event-history" style={{ maxWidth: '100%', maxHeight: 300 }}>
          <div className="olm-event-history-title">{t('olmAllEvents', { count: matchEvents.length })}</div>
          <div className="olm-event-list">
            {[...matchEvents].sort((a, b) => a.minute - b.minute || a.second - b.second).map(e => {
              const sysLabel = SYSTEM_EVENT_IDS.includes(Number(e.event_type));
              return (
                <div key={e.id} className={`olm-event-item ${e.team_id === homeTeamId ? 'olm-event-home' : e.team_id === guestTeamId ? 'olm-event-guest' : ''}`}>
                  <span className="olm-event-time">{formatEventMinuteLabel(e.minute, e.second)}'</span>
                  <div className="olm-event-info">
                    {sysLabel ? (
                      <span className="olm-event-label">{eventLabel(e.event_type, t)}</span>
                    ) : (
                      <>
                        <span className="olm-event-label">{eventLabel(e.event_type, t)}</span>
                        <span className="olm-event-player">{e.player_name}</span>
                        {e.assist_player_name && <span className="olm-event-assist">{t('olmPass', { name: e.assist_player_name })}</span>}
                        {e.event_type === 15 && e.additional_player_name && <span className="olm-event-assist">➜ {e.additional_player_name}</span>}
                        {e.comment ? <span className="olm-event-note">{e.comment}</span> : null}
                        <span className="olm-event-team">{e.team_name}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="olm-phase-actions">
          <div className="olm-btn olm-btn-secondary" onClick={handleClose}>{t('olmCloseNoSave')}</div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // RENDER: EVENT MODAL
  // ═══════════════════════════════════════

  const renderEventModal = () => {
    const teamId = eventModalTeamSide === 'home' ? homeTeamId : guestTeamId;
    const teamPlayers = getSelectedTeamPlayers(teamId);
    const typeInfo = eventTypeById(eventType);
    const needsPlayer = Boolean(typeInfo?.needsPlayer);
    const showAssist = Boolean(typeInfo?.needsSecond) && Number(eventType) !== 15;
    const isSub = Number(eventType) === 15;
    const assistPlayers = showAssist ? teamPlayers.filter(p => p.id !== parseInt(eventPlayer)) : [];
    const benchForSub = isSub ? getLiveBenchPlayers(eventModalTeamSide) : [];
    const isEditing = !!editingEventId;
    const canSave = (!needsPlayer || eventPlayer) && (!isSub || eventSubPlayer);

    return (
      <div className="olm-event-modal-overlay" onClick={() => { setEventModalOpen(false); setEditingEventId(null); }}>
        <div className="olm-event-modal" onClick={e => e.stopPropagation()}>
          <div className="olm-event-modal-header">
            <span>{isEditing ? t('olmEditEvent') : t('olmAddEvent')} — {eventModalTeamSide === 'home' ? matchData.home_team_name : matchData.guest_team_name}</span>
            <span className="olm-event-modal-time">{formatTime(displaySeconds)}</span>
          </div>
          <div className="olm-event-modal-body">
            <div className="olm-event-form-row">
              <label>{t('mrEventType')}</label>
              <select className="olm-select" value={eventType} onChange={e => { setEventType(parseInt(e.target.value)); setEventAssist(''); setEventSubPlayer(''); }}>
                {MATCH_EVENT_TYPES.map((item) => (
                  <option key={item.id} value={item.id}>{eventLabel(item.id, t)}</option>
                ))}
              </select>
            </div>

            {/* Team side tabs */}
            <div className="olm-event-form-row">
              <label>{t('team')}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <div
                  className={`olm-btn ${eventModalTeamSide === 'home' ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                  style={{ padding: '5px 14px', fontSize: 13, flex: 1 }}
                  onClick={() => { setEventModalTeamSide('home'); setEventPlayer(''); setEventAssist(''); }}
                >
                  {matchData.home_team_name}
                </div>
                <div
                  className={`olm-btn ${eventModalTeamSide === 'guest' ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                  style={{ padding: '5px 14px', fontSize: 13, flex: 1 }}
                  onClick={() => { setEventModalTeamSide('guest'); setEventPlayer(''); setEventAssist(''); }}
                >
                  {matchData.guest_team_name}
                </div>
              </div>
            </div>

            {/* Player — обязателен только для замены; для гола/карточек/ударов можно без автора */}
            <div className="olm-event-form-row">
              <label>{needsPlayer ? `${t('player')} *` : t('player')}</label>
              <select className="olm-select" value={eventPlayer} onChange={e => setEventPlayer(e.target.value)}>
                <option value="">{needsPlayer ? t('olmSelectPlayer') : t('mrSelect')}</option>
                {teamPlayers.map(p => (
                  <option key={p.id} value={p.id}>#{p.player_number} {p.family} {p.name}</option>
                ))}
              </select>
            </div>

            {showAssist && (
              <div className="olm-event-form-row">
                <label>{t('olmAssistOptional')}</label>
                <select className="olm-select" value={eventAssist} onChange={e => setEventAssist(e.target.value)}>
                  <option value="">{t('olmNoAssist')}</option>
                  {assistPlayers.map(p => (
                    <option key={p.id} value={p.id}>#{p.player_number} {p.family} {p.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Substitution: player IN */}
            {isSub && (
              <div className="olm-event-form-row">
                <label>{t('olmSubIn')} *</label>
                <select className="olm-select" value={eventSubPlayer} onChange={e => setEventSubPlayer(e.target.value)}>
                  <option value="">{t('olmSelectBench')}</option>
                  {benchForSub.map(p => (
                    <option key={p.id} value={p.id}>#{p.player_number} {p.family} {p.name}</option>
                  ))}
                </select>
                {eventPlayer && eventSubPlayer && (
                  <div className="olm-sub-hint">
                    🔄 {findPlayerById(parseInt(eventPlayer))?.family} ➜ {findPlayerById(parseInt(eventSubPlayer))?.family}
                  </div>
                )}
              </div>
            )}

            {/* Time */}
            <div className="olm-event-form-row">
              <label>{t('olmMinuteSecond')}</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="number" className="olm-select" style={{ width: 80 }} value={eventMinute} onChange={e => setEventMinute(e.target.value)} min="0" placeholder={t('olmMinPh')} />
                <span style={{ fontWeight: 700 }}>:</span>
                <input type="number" className="olm-select" style={{ width: 80 }} value={eventSecond} onChange={e => setEventSecond(e.target.value)} min="0" max="59" placeholder={t('olmSecPh')} />
              </div>
            </div>
            <div className="olm-event-form-row">
              <label>{t('comment')}</label>
              <input
                className="olm-select"
                value={eventComment}
                onChange={(e) => setEventComment(e.target.value)}
                maxLength={255}
              />
            </div>
          </div>
          <div className="olm-event-modal-footer">
            <div className="olm-btn olm-btn-secondary" onClick={() => { setEventModalOpen(false); setEditingEventId(null); }}>{t('cancel')}</div>
            {isEditing && (
              <div className="olm-btn olm-btn-danger" style={{ padding: '10px 16px' }} onClick={() => { removeEvent(editingEventId); setEventModalOpen(false); setEditingEventId(null); }}>{t('delete')}</div>
            )}
            <div className={`olm-btn ${canSave ? 'olm-btn-primary' : 'olm-btn-disabled'}`} onClick={saveEvent}>
              {isEditing ? t('save') : t('add')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════

  if (!visible || !matchData) return null;

  return (
    <div className="olm-modal-overlay">
      <div className="olm-modal-content">
        <div className="olm-container">
          {/* Header */}
          <div className="olm-header">
            <h2 className="olm-title">{t('mrOnlineMatch')}</h2>
            <span className="olm-header-info">{matchData.league_name} — {matchData.home_team_name} vs {matchData.guest_team_name}</span>
            <div className="olm-header-close" onClick={handleClose}>✕</div>
          </div>

          {/* Notification */}
          {notification && <div className="olm-notification">{notification}</div>}

          {/* Body */}
          <div className="olm-body">
            {isRestoring ? (
              <div className="olm-restoring">
                {t('olmLoadingServer')}
              </div>
            ) : (
              <>
                {(phase === 'roster' || phase === 'lineup') && renderSetupPhase()}
                {phase === 'live' && renderLivePhase()}
                {phase === 'ended' && renderEndedPhase()}
              </>
            )}
          </div>
        </div>

        {/* Event modal overlay */}
        {eventModalOpen && renderEventModal()}
      </div>
    </div>
  );
}
